import React, { Fragment, useState, useRef } from 'react';
import { string, bool, oneOf, arrayOf, shape } from 'prop-types';
import classnames from 'classnames';
import {
    CaretButton,
    Brand,
    Bubbles,
    Gallery,
    ORIENTATION,
} from './components';
import { getNextImageIndex } from './ImageBlock.utils';
import './ImageBlock.scss';

export const MODE = {
    DEFAULT: 'default',
    VISUAL: 'visual',
};

export default function ImageBlock({
    className,
    classNames,
    mainImage,
    thumbnails,
    brandName,
    brandImage,
    lazyload,
    mode,
    onSale,
    cdn,
    platform,
}) {
    const [imageIndex, setImageIndex] = useState(0);
    const [galleryClassnames] = useState({
        galleryLazyload: classNames.galleryLazyload,
    });

    const galleryElement = useRef(null);

    const handleImageIndexChange = (event, orientation) => {
        event.preventDefault();
        const nextImageIndex = getNextImageIndex(
            orientation,
            thumbnails.length,
            imageIndex,
        );

        if (Number.isInteger(nextImageIndex)) {
            setImageIndex(nextImageIndex);

            if (galleryElement.current) {
                galleryElement.current.scroll(
                    nextImageIndex * galleryElement.current.offsetWidth,
                    0,
                );
            }
        }
    };

    const handleGalleryChange = (evt, activeIndex) => {
        setImageIndex(activeIndex);
    };

    const rootClassnames = classnames('image-block', className);

    const imageBlockGalleryClassNames = classnames('image-block__gallery', {
        'image-block__gallery--visual': mode === MODE.VISUAL,
    });

    const salesPeriodClassnames = classnames(
        'image-block__icon-sale-period',
        'icon icon__sale',
        `icon__sale--${platform.PLATFORM.toLowerCase()}`,
    );

    return (
        <div className={rootClassnames}>
            {(brandImage || brandName) && (
                <Brand
                    className="image-block__brand"
                    image={brandImage}
                    name={brandName}
                    cdn={cdn}
                    lazyload={lazyload}
                />
            )}
            <Gallery
                className={imageBlockGalleryClassNames}
                classNames={galleryClassnames}
                mainImage={mainImage}
                thumbnails={thumbnails}
                activeThumbnailIndex={imageIndex}
                cdn={cdn}
                lazyload={lazyload}
                mode={mode}
                onChange={handleGalleryChange}
            />

            {onSale ? <i className={salesPeriodClassnames} /> : null}

            {thumbnails.length > 1 && (
                <Fragment>
                    <CaretButton
                        className="image-block__caret image-block__caret--left"
                        orientation={ORIENTATION.LEFT}
                        onClick={handleImageIndexChange}
                    />
                    <CaretButton
                        className="image-block__caret image-block__caret--right"
                        orientation={ORIENTATION.RIGHT}
                        onClick={handleImageIndexChange}
                    />
                    <Bubbles
                        className="image-block__bubbles"
                        length={thumbnails.length}
                        activeIndex={imageIndex}
                    />
                </Fragment>
            )}
        </div>
    );
}

ImageBlock.propTypes = {
    className: string,
    classNames: shape({
        galleryLazyload: string,
    }),
    lazyload: bool,
    mainImage: string.isRequired,
    thumbnails: arrayOf(string),
    brandName: string,
    brandImage: string,
    mode: oneOf([MODE.DEFAULT, MODE.VISUAL]),
    onSale: bool,
    cdn: shape({ CDN: string, CDN_IMAGE: string }).isRequired,
    platform: shape({ PLATFORM: string }).isRequired,
};

ImageBlock.defaultProps = {
    className: null,
    classNames: {},
    lazyload: false,
    mode: MODE.DEFAULT,
    onSale: false,
    thumbnails: [],
    brandName: null,
    brandImage: null,
};
